import { useState, useCallback, useRef, useMemo } from 'react';

/**
 * Hook for tracking row changes in a grid
 *
 * @param {Object} options
 * @param {Function} options.getRowId - Function to get row ID from data
 * @param {Function} [options.isNewRow] - Function to check if row is new
 * @returns {Object} Change tracking state and handlers
 *
 * @example
 * const {
 *   updatedRows,
 *   trackChange,
 *   trackNewRow,
 *   clearChanges,
 *   getChangedRowCount,
 *   hasChanges,
 *   getChangedData,
 * } = useChangeTracking({
 *   getRowId: (data) => data.BLOTTER_ID,
 *   isNewRow: (data) => data.BLOTTER_ID.startsWith('new_row_'),
 * });
 */
export const useChangeTracking = ({
  getRowId,
  isNewRow,
}) => {
  const [updatedRows, setUpdatedRows] = useState({});

  /**
   * Track a cell value change
   */
  const trackChange = useCallback((data, field, oldValue, newValue) => {
    const rowId = getRowId(data);

    setUpdatedRows(prev => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        [field]: {
          oldValue,
          newValue,
          timestamp: new Date().toISOString(),
        },
      },
    }));
  }, [getRowId]);

  /**
   * Track a new row
   */
  const trackNewRow = useCallback((data) => {
    const rowId = getRowId(data);

    setUpdatedRows(prev => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        _isNewRow: {
          oldValue: null,
          newValue: true,
          timestamp: new Date().toISOString(),
        },
      },
    }));
  }, [getRowId]);

  /**
   * Clear all tracked changes
   */
  const clearChanges = useCallback((rowIds = null) => {
    if (rowIds === null) {
      setUpdatedRows({});
    } else {
      setUpdatedRows(prev => {
        const updated = { ...prev };
        rowIds.forEach(id => delete updated[id]);
        return updated;
      });
    }
  }, []);

  /**
   * Get count of changed rows
   */
  const getChangedRowCount = useCallback(() => {
    return Object.keys(updatedRows).length;
  }, [updatedRows]);

  /**
   * Check if a specific row has changes
   */
  const isRowChanged = useCallback((data) => {
    const rowId = getRowId(data);
    return Boolean(updatedRows[rowId]);
  }, [getRowId, updatedRows]);

  /**
   * Check if there are any changes
   */
  const hasChanges = useMemo(() => {
    return Object.keys(updatedRows).length > 0;
  }, [updatedRows]);

  /**
   * Get rows with changes from row data
   */
  const getChangedData = useCallback((rowData) => {
    return rowData.filter(row => {
      const rowId = getRowId(row);
      const rowIsNew = isNewRow ? isNewRow(row) : false;
      return updatedRows[rowId] || rowIsNew;
    });
  }, [getRowId, isNewRow, updatedRows]);

  /**
   * Handle AG Grid cell value changed event
   */
  const handleCellValueChanged = useCallback((params) => {
    const { data, colDef, oldValue, newValue } = params;
    trackChange(data, colDef.field, oldValue, newValue);
  }, [trackChange]);

  return {
    updatedRows,
    trackChange,
    trackNewRow,
    clearChanges,
    getChangedRowCount,
    isRowChanged,
    hasChanges,
    getChangedData,
    handleCellValueChanged,
    setUpdatedRows,
  };
};

/**
 * Hook for row selection with Set-based tracking
 *
 * @param {Object} options
 * @param {Function} options.getRowId - Function to get row ID from data
 * @returns {Object} Selection state and handlers
 */
export const useRowSelection = ({ getRowId }) => {
  const [selectedRows, setSelectedRows] = useState(new Set());

  /**
   * Toggle selection for a single row
   */
  const toggleRowSelection = useCallback((data) => {
    const rowId = getRowId(data);

    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, [getRowId]);

  /**
   * Select multiple rows
   */
  const selectRows = useCallback((dataArray) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      dataArray.forEach(data => {
        newSet.add(getRowId(data));
      });
      return newSet;
    });
  }, [getRowId]);

  /**
   * Deselect multiple rows
   */
  const deselectRows = useCallback((dataArray) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      dataArray.forEach(data => {
        newSet.delete(getRowId(data));
      });
      return newSet;
    });
  }, [getRowId]);

  /**
   * Select all visible rows
   */
  const selectAllVisible = useCallback((gridApi) => {
    const visibleRowIds = new Set();
    gridApi.forEachNodeAfterFilter((node) => {
      if (node.data) {
        visibleRowIds.add(getRowId(node.data));
      }
    });
    setSelectedRows(visibleRowIds);
  }, [getRowId]);

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  /**
   * Check if a row is selected
   */
  const isSelected = useCallback((data) => {
    return selectedRows.has(getRowId(data));
  }, [getRowId, selectedRows]);

  /**
   * Get selected row count
   */
  const selectedCount = useMemo(() => {
    return selectedRows.size;
  }, [selectedRows]);

  /**
   * Get selected data from row data
   */
  const getSelectedData = useCallback((rowData) => {
    return rowData.filter(row => selectedRows.has(getRowId(row)));
  }, [getRowId, selectedRows]);

  return {
    selectedRows,
    toggleRowSelection,
    selectRows,
    deselectRows,
    selectAllVisible,
    clearSelection,
    isSelected,
    selectedCount,
    getSelectedData,
    setSelectedRows,
  };
};

/**
 * Hook for grid operations with ref
 *
 * @returns {Object} Grid ref and operation handlers
 */
export const useGridOperations = () => {
  const gridRef = useRef(null);

  /**
   * Export to Excel
   */
  const exportToExcel = useCallback((params = {}) => {
    const defaultParams = {
      fileName: `export_${new Date().toISOString().split('T')[0]}.xlsx`,
      sheetName: 'Data',
      exportMode: 'xlsx',
      skipColumnHeaders: false,
      skipColumnGroupHeaders: false,
      skipPinnedTop: false,
      skipPinnedBottom: false,
    };

    gridRef.current?.api?.exportDataAsExcel({
      ...defaultParams,
      ...params,
    });
  }, []);

  /**
   * Export only selected rows
   */
  const exportSelected = useCallback((params = {}) => {
    exportToExcel({
      ...params,
      onlySelected: true,
    });
  }, [exportToExcel]);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setFilterModel(null);
      // A user action: report it as one, or useGridConfigs reads the default
      // 'api' source as programmatic and leaves the old filter in the stash.
      gridRef.current.api.onFilterChanged('columnFilter');
    }
  }, []);

  /**
   * Auto-size all columns
   */
  const autoSizeColumns = useCallback(() => {
    gridRef.current?.api?.autoSizeAllColumns();
  }, []);

  /**
   * Refresh cells
   */
  const refreshCells = useCallback((params = {}) => {
    gridRef.current?.api?.refreshCells(params);
  }, []);

  /**
   * Get filtered row data
   */
  const getFilteredData = useCallback(() => {
    const data = [];
    gridRef.current?.api?.forEachNodeAfterFilter((node) => {
      if (node.data) {
        data.push(node.data);
      }
    });
    return data;
  }, []);

  /**
   * Get all row data
   */
  const getAllData = useCallback(() => {
    const data = [];
    gridRef.current?.api?.forEachNode((node) => {
      if (node.data) {
        data.push(node.data);
      }
    });
    return data;
  }, []);

  return {
    gridRef,
    exportToExcel,
    exportSelected,
    clearFilters,
    autoSizeColumns,
    refreshCells,
    getFilteredData,
    getAllData,
  };
};

/**
 * Hook for clipboard operations
 *
 * @param {Object} options
 * @param {Function} [options.onCopy] - Callback when copy happens
 * @param {Function} [options.onPaste] - Callback when paste happens
 * @returns {Object} Clipboard handlers
 */
export const useClipboard = ({ onCopy, onPaste } = {}) => {
  /**
   * Handle keyboard events for copy/paste
   */
  const handleCellKeyDown = useCallback((params) => {
    const { event, api } = params;

    // Handle Ctrl+C for copying
    if (event.ctrlKey && event.key === 'c') {
      event.preventDefault();
      const cellRanges = api.getCellRanges();

      if (cellRanges && cellRanges.length > 0) {
        api.copySelectedRangeToClipboard();
        onCopy?.('range');
      } else {
        api.copySelectedRangeToClipboard();
        onCopy?.('cell');
      }
    }

    // Handle Ctrl+V for pasting
    if (event.ctrlKey && event.key === 'v') {
      event.preventDefault();
      api.pasteFromClipboard();
      onPaste?.();
    }

    // Handle Ctrl+X for cutting
    if (event.ctrlKey && event.key === 'x') {
      event.preventDefault();
      api.copySelectedRangeToClipboard();
      // Note: Actual cut behavior (clearing cells) would need custom implementation
    }
  }, [onCopy, onPaste]);

  /**
   * Handle paste start
   */
  const handlePasteStart = useCallback(() => {
    console.log('Paste operation started');
  }, []);

  /**
   * Handle paste end
   */
  const handlePasteEnd = useCallback((params) => {
    console.log('Paste operation completed');
    onPaste?.(params);
  }, [onPaste]);

  return {
    handleCellKeyDown,
    handlePasteStart,
    handlePasteEnd,
  };
};

/**
 * Hook for new row management
 *
 * @param {Object} options
 * @param {Array} options.fields - Fields to include in new row
 * @param {Object} [options.defaults={}] - Default values for fields
 * @param {Function} options.onNewRow - Callback to add new row to data
 * @param {Function} options.trackNewRow - Function to track new row
 * @param {string} [options.userId=''] - Current user ID
 * @returns {Object} New row handlers
 */
export const useNewRow = ({
  fields,
  defaults = {},
  onNewRow,
  trackNewRow,
  userId = '',
}) => {
  const [newRowCount, setNewRowCount] = useState(0);

  /**
   * Create a new blank row
   */
  const createBlankRow = useCallback(() => {
    const nextCount = newRowCount + 1;
    setNewRowCount(nextCount);

    const newRow = {};
    fields.forEach(field => {
      if (defaults[field] !== undefined) {
        newRow[field] = typeof defaults[field] === 'function'
          ? defaults[field](nextCount)
          : defaults[field];
      } else {
        newRow[field] = '';
      }
    });

    // Set modification tracking
    if (fields.includes('MODIFIED_BY')) {
      newRow.MODIFIED_BY = userId;
    }
    if (fields.includes('MODIFIED_DATE')) {
      newRow.MODIFIED_DATE = new Date().toISOString();
    }

    onNewRow(newRow);
    trackNewRow?.(newRow);

    return newRow;
  }, [newRowCount, fields, defaults, onNewRow, trackNewRow, userId]);

  /**
   * Duplicate an existing row
   */
  const duplicateRow = useCallback((sourceData, fieldsToCopy = []) => {
    const nextCount = newRowCount + 1;
    setNewRowCount(nextCount);

    const newRow = {};
    fields.forEach(field => {
      if (defaults[field] !== undefined) {
        newRow[field] = typeof defaults[field] === 'function'
          ? defaults[field](nextCount)
          : defaults[field];
      } else if (fieldsToCopy.includes(field)) {
        newRow[field] = sourceData[field];
      } else {
        newRow[field] = '';
      }
    });

    // Set modification tracking
    if (fields.includes('MODIFIED_BY')) {
      newRow.MODIFIED_BY = userId;
    }
    if (fields.includes('MODIFIED_DATE')) {
      newRow.MODIFIED_DATE = new Date().toISOString();
    }

    onNewRow(newRow);
    trackNewRow?.(newRow);

    return newRow;
  }, [newRowCount, fields, defaults, onNewRow, trackNewRow, userId]);

  /**
   * Reset new row counter
   */
  const resetCounter = useCallback(() => {
    setNewRowCount(0);
  }, []);

  return {
    newRowCount,
    createBlankRow,
    duplicateRow,
    resetCounter,
    setNewRowCount,
  };
};

/**
 * Hook for validation
 *
 * @param {Object} options
 * @param {Object} options.rules - Validation rules by field
 * @param {Array} [options.requiredFields=[]] - Required field names
 * @returns {Object} Validation handlers
 */
export const useValidation = ({
  rules = {},
  requiredFields = [],
}) => {
  const [validationErrors, setValidationErrors] = useState([]);

  /**
   * Validate a single row
   */
  const validateRow = useCallback((row, rowIndex) => {
    const errors = [];

    // Check required fields
    requiredFields.forEach(field => {
      const value = row[field];
      if (value === null || value === undefined || value === '') {
        errors.push({
          row: rowIndex,
          field,
          message: `${field} is required`,
        });
      }
    });

    // Check custom rules
    Object.entries(rules).forEach(([field, rule]) => {
      const value = row[field];
      if (typeof rule === 'function') {
        const error = rule(value, row);
        if (error) {
          errors.push({
            row: rowIndex,
            field,
            message: error,
          });
        }
      }
    });

    return errors;
  }, [rules, requiredFields]);

  /**
   * Validate multiple rows
   */
  const validateRows = useCallback((rows) => {
    const allErrors = [];

    rows.forEach((row, index) => {
      const rowErrors = validateRow(row, index);
      allErrors.push(...rowErrors);
    });

    setValidationErrors(allErrors);
    return allErrors;
  }, [validateRow]);

  /**
   * Clear validation errors
   */
  const clearErrors = useCallback(() => {
    setValidationErrors([]);
  }, []);

  /**
   * Check if validation passed
   */
  const isValid = useMemo(() => {
    return validationErrors.length === 0;
  }, [validationErrors]);

  return {
    validationErrors,
    validateRow,
    validateRows,
    clearErrors,
    isValid,
    setValidationErrors,
  };
};

export default {
  useChangeTracking,
  useRowSelection,
  useGridOperations,
  useClipboard,
  useNewRow,
  useValidation,
};
